import { apiRequest, queryString } from "./http";
import type { AuthorizeUrlResponse, CancelRunResponse, DisconnectedResponse, ImportXeroEmployeesPayload, Integration, QueuedSync, Schedule, SchedulePayload, SchedulerHealth, StartXeroConnectPayload, SyncRun, SyncRunDetail, SyncedInvoice, UpdateXeroConnectionAccess, XeroConnection, UpdateXeroCapabilityGrants, XeroEmployeePreview, XeroEmployeeReconciliation, XeroEmployeeImportResult, XeroPermissions, XeroStatus, XeroTimesheetExportResult } from "./generated/openapi";
export type { ImportXeroEmployeesPayload, Integration, QueuedSync, Schedule, SchedulerHealth, ScheduleSlot, StartXeroConnectPayload, SyncRun, SyncRunLine, SyncedInvoice, XeroAccess, XeroConnection, XeroCapabilityDefinition, XeroEmployeePreview, XeroEmployeeReconciliation, XeroEmployeeImportResult, XeroPermissions, XeroStatus, XeroTimesheetExportResult } from "./generated/openapi";

export const xeroApi = {
  status: () => apiRequest<XeroStatus>("/api/xero/status"),

  /** Returns the URL to send the browser to; the redirect is not an XHR. */
  connect: (payload: StartXeroConnectPayload) =>
    apiRequest<AuthorizeUrlResponse>("/api/xero/connect", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  saveConnectionAccess: (tenantId: string, payload: UpdateXeroConnectionAccess) =>
    apiRequest<XeroConnection>(`/api/xero/connections/${tenantId}/access`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),

  disconnect: (tenantId: string) =>
    apiRequest<DisconnectedResponse>(`/api/xero/connections/${tenantId}`, {
      method: "DELETE",
    }),

  listIntegrations: () => apiRequest<Integration[]>("/api/xero/integrations"),

  listRuns: (integration?: string) =>
    apiRequest<SyncRun[]>(`/api/xero/runs${queryString({ integration })}`),

  getRun: (runId: string) =>
    apiRequest<SyncRunDetail>(`/api/xero/runs/${runId}`),

  listInvoices: () => apiRequest<SyncedInvoice[]>("/api/xero/invoices"),

  previewEmployees: (tenantId: string, signal?: AbortSignal) =>
    apiRequest<XeroEmployeePreview[]>(`/api/xero/employees/${tenantId}`, { signal }),

  importEmployees: (tenantId: string, payload: ImportXeroEmployeesPayload) =>
    apiRequest<XeroEmployeeImportResult>(`/api/xero/employees/import/${tenantId}`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  reconcileEmployees: (tenantId: string) =>
    apiRequest<XeroEmployeeReconciliation>(`/api/xero/employees/reconcile/${tenantId}`),

  permissions: (signal?: AbortSignal) => apiRequest<XeroPermissions>("/api/xero/permissions", { signal }),

  savePermissions: (payload: UpdateXeroCapabilityGrants) =>
    apiRequest<XeroPermissions>("/api/xero/permissions", {
      method: "PUT",
      body: JSON.stringify(payload),
    }),

  exportTimesheet: (timesheetId: string) =>
    apiRequest<XeroTimesheetExportResult>(`/api/xero/timesheets/${timesheetId}/export`, {
      method: "POST",
    }),

  /** Queues whichever integration is being viewed, not always the employee one. */
  runIntegration: (integration: string) =>
    apiRequest<QueuedSync>(`/api/xero/integrations/${integration}/run`, {
      method: "POST",
    }),

  cancelRun: (runId: string) =>
    apiRequest<CancelRunResponse>(
      `/api/xero/runs/${runId}/cancel`,
      { method: "POST" },
    ),

  listSchedules: () => apiRequest<Schedule[]>("/api/xero/schedules"),

  saveSchedule: (
    integration: string,
    payload: SchedulePayload,
  ) =>
    apiRequest<Schedule>(`/api/xero/schedules/${integration}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),

  schedulerHealth: () => apiRequest<SchedulerHealth>("/api/xero/scheduler"),
};
